(function () {
  const selectors = {
    addonsRoot: '[data-cp-product-addons]',
    addonsList: '[data-cp-addon-list]',
    addonCard: '[data-cp-addon-card]',
    addonToggle: '[data-cp-addon-toggle]',
    addonVariant: '[data-cp-addon-variant]',
    addonSwatch: '[data-cp-addon-swatch]',
    addonPriceCurrent: '[data-cp-addon-price-current]',
    addonPriceCompare: '[data-cp-addon-price-compare]',
    addonStatus: '[data-cp-addon-status]',
    addonImageLink: '[data-cp-addon-image-link]',
    sectionStoreHost: 'section-store--product-addons-block',
    addToCartButton: '.add_to_cart',
    ajaxSubmit: '.ajax-submit',
    stickyForm: 'form[id^="sticky-product-form-form-"]',
    stickyButton: '[data-cp-sticky-add-to-cart]',
    stickyButtonText: '[data-cp-sticky-add-to-cart-text]',
    stickyButtonPrice: '[data-cp-sticky-add-to-cart-price]',
    // Clicking any of these inside a card is the shopper operating that
    // control, not choosing the add-on, so the row-tap handler leaves them be.
    rowTapExempt: 'a, button, select, input, label, textarea, [role="button"]'
  };

  function parseBoolean(value) {
    return value === true || value === 'true';
  }

  function getClosestProductScope(node) {
    if (!node || typeof node.closest !== 'function') {
      return null;
    }

    return node.closest('.product-main') || node.closest('.js-product-section') || node.closest('.shopify-section');
  }

  function isStickyProductForm(node) {
    const form = node instanceof HTMLFormElement
      ? node
      : (node && typeof node.closest === 'function' ? node.closest('form') : null);

    if (!form) {
      return false;
    }

    // getAttribute, not .id — every product form carries <input name="id"> for
    // the variant, and HTML named-property access makes form.id resolve to
    // that input element rather than the form's id string. The old
    // `typeof form.id === 'string'` guard meant this branch never once
    // matched, which is why the sticky bar silently skipped add-ons.
    const formId = form.getAttribute('id') || '';

    return formId.indexOf('sticky-product-form-form-') === 0;
  }

  function getAddonsRootForNode(node) {
    const scope = getClosestProductScope(node);
    const scopedRoot = scope && scope.querySelector(selectors.addonsRoot);

    if (scopedRoot) {
      return scopedRoot;
    }

    // The mobile sticky bar lives in its own section, so the add-ons block is
    // never inside its scope. Without this fallback a shopper could tick an
    // add-on, buy from the sticky bar, and silently get only the main product.
    if (isStickyProductForm(node)) {
      return document.querySelector(selectors.addonsRoot);
    }

    return null;
  }

  function getSelectedAddonCards(addonsRoot) {
    if (!addonsRoot) {
      return [];
    }

    return Array.from(addonsRoot.querySelectorAll(selectors.addonCard)).filter(card => {
      const checkbox = card.querySelector(selectors.addonToggle);
      return checkbox && checkbox.checked && !checkbox.disabled;
    });
  }

  function getSectionStoreHostsForNode(node) {
    const scope = getClosestProductScope(node);
    const scopedHosts = scope ? Array.from(scope.querySelectorAll(selectors.sectionStoreHost)) : [];

    if (scopedHosts.length) {
      return scopedHosts;
    }

    if (isStickyProductForm(node)) {
      return Array.from(document.querySelectorAll(selectors.sectionStoreHost));
    }

    return [];
  }

  function getSelectedSectionStoreAddonItems(node) {
    const itemsByVariantId = new Map();

    getSectionStoreHostsForNode(node).forEach(host => {
      const shadowRoot = host && host.shadowRoot;

      if (!shadowRoot) {
        return;
      }

      shadowRoot.querySelectorAll('.addons-box').forEach(box => {
        const toggle = box.querySelector('.addon-toggle-input');

        if (!(toggle && toggle.checked && !toggle.disabled)) {
          return;
        }

        const variantButton = box.querySelector('.product-variant-selector button');
        const variantId = String(
          (variantButton && variantButton.dataset && variantButton.dataset.variantId)
          || box.dataset.variantId
          || ''
        ).trim();

        if (!variantId || itemsByVariantId.has(variantId)) {
          return;
        }

        itemsByVariantId.set(variantId, {
          id: variantId,
          quantity: 1
        });
      });
    });

    return Array.from(itemsByVariantId.values());
  }

  // Drop trailing zero decimals to match the Calypso no-trailing-zeros style
  // ($15.00 -> $15, but $15.50 stays). Handles either . or , as the separator,
  // and won't touch the thousands group (negative lookahead for another digit).
  function stripZeroCents(formatted) {
    return String(formatted).replace(/([.,])00(?!\d)/, '');
  }

  function formatMoney(cents, moneyFormat) {
    const safeCents = Number(cents) || 0;
    const format = moneyFormat || document.body.dataset.moneyFormat || '${{amount}}';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return stripZeroCents(window.Shopify.formatMoney(safeCents, format));
    }

    const amount = safeCents / 100;
    const currency = document.body.dataset.shopCurrency || 'USD';

    return stripZeroCents(new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(amount));
  }

  function syncToggleState(card) {
    if (!card) {
      return;
    }

    const checkbox = card.querySelector(selectors.addonToggle);
    const isSelected = checkbox && checkbox.checked && !checkbox.disabled;

    card.classList.toggle('is-selected', Boolean(isSelected));
  }

  function syncAvailability(card, available) {
    if (!card) {
      return;
    }

    const isAvailable = parseBoolean(available);
    const checkbox = card.querySelector(selectors.addonToggle);
    const status = card.querySelector(selectors.addonStatus);

    card.classList.toggle('is-unavailable', !isAvailable);

    if (checkbox) {
      checkbox.disabled = !isAvailable;

      if (!isAvailable) {
        checkbox.checked = false;
      }
    }

    if (status) {
      status.hidden = isAvailable;
    }

    syncToggleState(card);
  }

  function updateAddonImage(card, imageUrl, lightboxUrl) {
    if (!card || !imageUrl) {
      return;
    }

    const image = card.querySelector('.cp-product-addon__image');
    const imageLink = card.querySelector(selectors.addonImageLink);

    if (!image) {
      return;
    }

    image.setAttribute('src', imageUrl);
    image.setAttribute('srcset', imageUrl);

    if (imageLink && lightboxUrl) {
      imageLink.setAttribute('href', lightboxUrl);
    }
  }

  function syncSwatchState(card, variantId) {
    if (!card) {
      return;
    }

    card.querySelectorAll(selectors.addonSwatch).forEach(swatch => {
      const isSelected = String(swatch.dataset.variantId || '') === String(variantId || '');
      swatch.classList.toggle('is-selected', isSelected);
      swatch.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  }

  function updateVariantCard(select, options = {}) {
    if (!select) {
      return;
    }

    const option = select.options[select.selectedIndex];
    const card = select.closest(selectors.addonCard);
    const preserveImage = Boolean(options && options.preserveImage);

    if (!card || !option) {
      return;
    }

    const currentPrice = card.querySelector(selectors.addonPriceCurrent);
    const comparePrice = card.querySelector(selectors.addonPriceCompare);
    const addonsRoot = card.closest(selectors.addonsRoot);
    const moneyFormat = (addonsRoot && addonsRoot.dataset.moneyFormat) || document.body.dataset.moneyFormat;
    const variantId = option.value;
    const price = Number(option.dataset.price || 0);
    const compareAtPrice = Number(option.dataset.comparePrice || 0);
    const isAvailable = parseBoolean(option.dataset.available);
    const showComparePrice = parseBoolean(card.dataset.showComparePrice);

    card.dataset.selectedVariantId = variantId;

    if (currentPrice) {
      currentPrice.textContent = formatMoney(price, moneyFormat);
    }

    if (comparePrice) {
      const shouldShowCompare = showComparePrice && compareAtPrice > price;
      comparePrice.hidden = !shouldShowCompare;
      comparePrice.textContent = shouldShowCompare ? formatMoney(compareAtPrice, moneyFormat) : '';
    }

    if (!preserveImage) {
      updateAddonImage(card, option.dataset.imageUrl, option.dataset.imageLightboxUrl);
    }

    card.dataset.selectedPrice = String(price);

    syncSwatchState(card, variantId);
    syncAvailability(card, isAvailable);

    if (addonsRoot) {
      renderPurchaseSummary(addonsRoot);
    }
  }

  // ---------------------------------------------------------------------
  // Purchase summary
  //
  // The add-to-cart button has to tell the truth about what a tap will put in
  // the bag: "Add to bag - $15" with nothing ticked, "Add both to bag - $29"
  // with one add-on ticked, "Add all 3 to bag - $43" with two.
  //
  // The theme's own variant script (assets/sections.js) owns that button and
  // rewrites both spans on every variant change, so we never try to hold the
  // line against it. Instead we keep the *base* state (the label and price the
  // theme most recently wrote) per button and re-derive the total from it.
  // ---------------------------------------------------------------------

  const buttonBaseState = new WeakMap();

  // Held while we write to a button, so the sticky-bar MutationObserver can
  // tell our own writes apart from the sticky component rebuilding its
  // innerHTML. Released on a timer, not synchronously: observer callbacks are
  // microtasks and would otherwise run after the flag had already cleared,
  // and we would answer our own write in a loop.
  let summaryWriteDepth = 0;

  function beginSummaryWrite() {
    summaryWriteDepth += 1;
  }

  function endSummaryWrite() {
    setTimeout(() => {
      summaryWriteDepth = Math.max(0, summaryWriteDepth - 1);
    }, 0);
  }

  function readBaseState(button) {
    let state = buttonBaseState.get(button);

    if (!state) {
      const declaredPrice = Number(button.dataset.cpBasePrice);

      state = {
        label: String(button.dataset.label || '').trim(),
        priceCents: Number.isFinite(declaredPrice) && declaredPrice > 0 ? declaredPrice : null,
        available: !(button.disabled || button.classList.contains('disabled'))
      };

      buttonBaseState.set(button, state);
    }

    return state;
  }

  function getSummaryTargets(addonsRoot) {
    const scope = getClosestProductScope(addonsRoot);
    const targets = [];

    if (scope) {
      scope.querySelectorAll(selectors.addToCartButton).forEach(button => {
        targets.push({
          button,
          textEl: button.querySelector('.text'),
          priceEl: button.querySelector('.add_to_cart_price'),
          separator: ' - '
        });
      });
    }

    // The sticky bar is a sibling section, so match it up by product id rather
    // than by containment. Guards against a featured-product block elsewhere on
    // the page reaching over and rewriting the sticky bar for a different pen.
    const productId = String(addonsRoot.dataset.productId || '').trim();

    document.querySelectorAll(selectors.stickyButton).forEach(button => {
      const host = button.closest('[data-product-id]');
      const hostProductId = host ? String(host.dataset.productId || '').trim() : '';

      if (productId && hostProductId && productId !== hostProductId) {
        return;
      }

      targets.push({
        button,
        textEl: button.querySelector(selectors.stickyButtonText),
        priceEl: button.querySelector(selectors.stickyButtonPrice),
        separator: ' · '
      });
    });

    return targets;
  }

  function getSelectedAddonPriceCents(addonsRoot) {
    return getSelectedAddonCards(addonsRoot).reduce((total, card) => {
      const cents = Number(card.dataset.selectedPrice);
      return total + (Number.isFinite(cents) ? cents : 0);
    }, 0);
  }

  // "Add to bag" -> "Add both to bag" (2 items) / "Add all 3 to bag" (3+).
  // A label that doesn't open with "Add" (a pre-order button, say) is left as
  // written; the running total still carries the message.
  function withItemCountLabel(baseLabel, itemCount) {
    if (!baseLabel || itemCount <= 1) {
      return baseLabel;
    }

    const qualifier = itemCount === 2 ? 'both' : `all ${itemCount}`;

    return baseLabel.replace(/^add\b/i, match => `${match} ${qualifier}`);
  }

  // Inverse of withItemCountLabel. Whenever we re-read a label out of the DOM
  // we may be reading one of our own, so peel any qualifier back off first —
  // that keeps the operation idempotent instead of compounding into
  // "Add both both to bag".
  function stripItemCountLabel(label) {
    return label.replace(/^(add)\s+(?:both|all\s+\d+)\b/i, '$1');
  }

  function renderPurchaseSummary(addonsRoot) {
    if (!addonsRoot) {
      return;
    }

    const addonCount = getSelectedAddonCards(addonsRoot).length;
    const addonCents = getSelectedAddonPriceCents(addonsRoot);
    const moneyFormat = addonsRoot.dataset.moneyFormat;

    beginSummaryWrite();

    try {
      getSummaryTargets(addonsRoot).forEach(target => {
        const base = readBaseState(target.button);

        // Sold out, or a price we were never told: leave whatever the theme
        // put there. A wrong total is worse than no total.
        if (!base.available || base.priceCents === null) {
          return;
        }

        const label = withItemCountLabel(base.label, addonCount + 1);

        if (target.textEl) {
          target.textEl.textContent = label;
        }

        if (target.priceEl) {
          target.priceEl.textContent = target.separator + formatMoney(base.priceCents + addonCents, moneyFormat);
        }
      });
    } finally {
      endSummaryWrite();
    }
  }

  // Re-read the base state straight out of the DOM after something else has
  // rewritten the button, then re-apply our total on top of it.
  function adoptBaseState(addonsRoot, variantPriceCents, isAvailable) {
    getSummaryTargets(addonsRoot).forEach(target => {
      const base = readBaseState(target.button);

      if (target.textEl) {
        const written = stripItemCountLabel(target.textEl.textContent.trim());

        if (written) {
          base.label = written;
        }
      }

      if (typeof isAvailable === 'boolean') {
        base.available = isAvailable;
      }

      if (Number.isFinite(variantPriceCents) && variantPriceCents > 0) {
        base.priceCents = variantPriceCents;
      }
    });

    renderPurchaseSummary(addonsRoot);
  }

  // The theme exposes its variant handler globally; wrap it rather than editing
  // assets/sections.js, so a theme update doesn't quietly drop this.
  function patchSelectCallback() {
    const original = window.selectCallback;

    if (typeof original !== 'function' || original.cpAddonsPatched) {
      return typeof original === 'function';
    }

    const patched = function (variant, selector, $element) {
      const result = original.apply(this, arguments);

      try {
        const node = $element && $element.length ? $element[0] : ($element instanceof Element ? $element : null);
        const scope = getClosestProductScope(node) || document;
        const available = Boolean(variant && variant.available);

        getAddonRoots(scope).forEach(addonsRoot => {
          adoptBaseState(addonsRoot, available ? Number(variant.price) : NaN, available);
        });
      } catch (error) {
        // Never let the summary break the theme's variant switching.
      }

      return result;
    };

    patched.cpAddonsPatched = true;
    window.selectCallback = patched;

    return true;
  }

  // The sticky bar's custom element rebuilds its button innerHTML on every
  // swatch change and never calls selectCallback, so watch it directly. It
  // stamps the new variant price on the button for us to read back.
  function observeStickyButtons() {
    document.querySelectorAll(selectors.stickyButton).forEach(button => {
      if (button.dataset.cpSummaryObserved === 'true') {
        return;
      }

      button.dataset.cpSummaryObserved = 'true';

      const observer = new MutationObserver(() => {
        if (summaryWriteDepth > 0) {
          return;
        }

        const declaredPrice = Number(button.dataset.cpBasePrice);
        const available = !button.disabled;

        getAddonRoots(document).forEach(addonsRoot => {
          adoptBaseState(addonsRoot, declaredPrice, available);
        });
      });

      observer.observe(button, {
        attributes: true,
        attributeFilter: ['data-cp-base-price', 'disabled'],
        childList: true,
        subtree: true,
        characterData: true
      });
    });
  }

  async function fetchRelatedAddonMarkup(addonsRoot, url) {
    if (!addonsRoot || !url) {
      return null;
    }

    const blockId = String(addonsRoot.dataset.blockId || '').trim();

    if (!blockId) {
      return null;
    }

    const response = await fetch(url, {
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error('Unable to load related add-ons.');
    }

    const markup = await response.text();
    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const fallbackRoot = parsed.querySelector(`${selectors.addonsRoot}[data-block-id="${blockId}"]`);
    const fallbackList = fallbackRoot && fallbackRoot.querySelector(selectors.addonsList);

    if (!(fallbackList && fallbackList.querySelector(selectors.addonCard))) {
      return null;
    }

    return fallbackList.innerHTML;
  }

  async function loadRelatedFallback(addonsRoot) {
    if (!addonsRoot || addonsRoot.querySelector(selectors.addonCard)) {
      if (addonsRoot) {
        addonsRoot.hidden = false;
      }
      return;
    }

    const list = addonsRoot.querySelector(selectors.addonsList);
    const relatedUrl = String(addonsRoot.dataset.relatedUrl || '').trim();
    const state = String(addonsRoot.dataset.relatedState || 'idle').trim();

    if (!list || !relatedUrl || state === 'loading' || state === 'loaded') {
      return;
    }

    addonsRoot.dataset.relatedState = 'loading';

    try {
      const relatedMarkup = await fetchRelatedAddonMarkup(addonsRoot, relatedUrl);

      if (!relatedMarkup) {
        addonsRoot.dataset.relatedState = 'loaded';
        return;
      }

      list.innerHTML = relatedMarkup;
      list.hidden = false;
      addonsRoot.hidden = false;
      addonsRoot.dataset.addonSource = 'related';

      const emptyState = addonsRoot.querySelector('.cp-product-addons__empty');

      if (emptyState) {
        emptyState.hidden = true;
      }

      initializeAddonCards(addonsRoot);
      addonsRoot.dataset.relatedState = 'loaded';
    } catch (error) {
      addonsRoot.dataset.relatedState = 'error';
    }
  }

  function getMainItemFromForm(form) {
    if (!form) {
      return null;
    }

    const formData = new FormData(form);
    const properties = {};
    const item = {
      id: '',
      quantity: 1
    };

    for (const [name, value] of formData.entries()) {
      if (typeof value !== 'string') {
        continue;
      }

      if (name === 'id') {
        item.id = value;
        continue;
      }

      if (name === 'quantity') {
        const parsedQuantity = parseInt(value, 10);
        item.quantity = Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
        continue;
      }

      if (name === 'selling_plan' && value) {
        item.selling_plan = value;
        continue;
      }

      if (name.startsWith('properties[') && name.endsWith(']')) {
        const propertyName = name.slice(11, -1);

        if (propertyName && value !== '') {
          properties[propertyName] = value;
        }
      }
    }

    if (Object.keys(properties).length) {
      item.properties = properties;
    }

    return item.id ? item : null;
  }

  function getAddonItems(addonsRoot, quantity) {
    return getSelectedAddonCards(addonsRoot)
      .map(card => {
        const variantId = card.dataset.selectedVariantId;

        if (!variantId) {
          return null;
        }

        return {
          id: variantId,
          quantity
        };
      })
      .filter(Boolean);
  }

  function getCombinedAddonItems(form, mainItem) {
    const itemsByVariantId = new Map();
    const addonsRoot = getAddonsRootForNode(form);
    const cpAddonItems = addonsRoot ? getAddonItems(addonsRoot, mainItem.quantity || 1) : [];
    const sectionStoreAddonItems = getSelectedSectionStoreAddonItems(form);

    cpAddonItems.concat(sectionStoreAddonItems).forEach(item => {
      const itemId = String(item && item.id ? item.id : '').trim();

      if (!itemId) {
        return;
      }

      itemsByVariantId.set(itemId, {
        id: itemId,
        quantity: Math.max(parseInt(item.quantity, 10) || 1, 1)
      });
    });

    return Array.from(itemsByVariantId.values());
  }

  function setButtonState(button, isBusy) {
    if (!button) {
      return;
    }

    if (isBusy) {
      button.setAttribute('disabled', 'disabled');
      button.setAttribute('aria-busy', 'true');
      button.classList.add('disabled');
    } else {
      button.removeAttribute('disabled');
      button.removeAttribute('aria-busy');
      button.classList.remove('disabled');
    }
  }

  function clearWarnings() {
    document.querySelectorAll('.warning').forEach(warning => warning.remove());
  }

  function showWarning(form, message) {
    if (!form || !message) {
      return;
    }

    const warning = document.createElement('p');
    warning.className = 'warning animated bounceIn';
    warning.textContent = message.replace('All 1 ', 'All ');
    form.insertAdjacentElement('afterend', warning);
  }

  function getBundlePayload(form) {
    const mainItem = getMainItemFromForm(form);

    if (!mainItem) {
      return null;
    }

    const addonItems = getCombinedAddonItems(form, mainItem);

    if (!addonItems.length) {
      return null;
    }

    return {
      mainItem,
      addonItems
    };
  }

  function getCartAddUrl() {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    return new URL('cart/add.js', `${window.location.origin}${root}`).toString();
  }

  async function submitBundle(form, button, bundlePayload) {
    const { mainItem, addonItems } = bundlePayload;
    const recipientForm = form.querySelector('[data-recipient-form]');

    clearWarnings();

    if (recipientForm) {
      recipientForm.classList.remove('recipient-form--has-errors');
    }

    setButtonState(button, true);

    try {
      const response = await fetch(getCartAddUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [mainItem].concat(addonItems)
        })
      });

      const payload = await response.json();

      if (!response.ok) {
        throw payload;
      }

      window.dispatchEvent(
        new CustomEvent('cart:item-added', {
          bubbles: true,
          detail: payload
        })
      );
    } catch (error) {
      if (error && error.errors && error.errors.email && recipientForm) {
        recipientForm.classList.add('recipient-form--has-errors');
      } else {
        showWarning(form, (error && error.description) || 'Unable to add these items to your cart.');
      }
    } finally {
      setButtonState(button, false);
    }
  }

  function maybeHandleForm(form, trigger) {
    if (!form) {
      return false;
    }

    const bundlePayload = getBundlePayload(form);

    if (!bundlePayload) {
      return false;
    }

    const addToCartButton =
      (trigger && trigger.closest && trigger.closest(selectors.addToCartButton)) ||
      form.querySelector(selectors.addToCartButton);

    submitBundle(form, addToCartButton, bundlePayload);
    return true;
  }

  document.addEventListener('change', event => {
    const variantSelect = event.target.closest(selectors.addonVariant);

    if (variantSelect) {
      updateVariantCard(variantSelect);
      return;
    }

    const toggle = event.target.closest(selectors.addonToggle);

    if (toggle) {
      const card = toggle.closest(selectors.addonCard);
      syncToggleState(card);
      renderPurchaseSummary(card && card.closest(selectors.addonsRoot));
    }
  });

  // Whole-row tap. Anything that is itself a control (the checkbox and its
  // label, the variant select, a swatch, the image lightbox link) handles its
  // own click; everywhere else on the card toggles the add-on.
  document.addEventListener('click', event => {
    const card = event.target.closest(selectors.addonCard);

    if (!card || event.target.closest(selectors.rowTapExempt)) {
      return;
    }

    const checkbox = card.querySelector(selectors.addonToggle);

    if (!checkbox || checkbox.disabled) {
      return;
    }

    checkbox.checked = !checkbox.checked;
    checkbox.dispatchEvent(new Event('change', { bubbles: true }));
  });

  document.addEventListener('click', event => {
    const swatch = event.target.closest(selectors.addonSwatch);

    if (!swatch || swatch.disabled) {
      return;
    }

    const card = swatch.closest(selectors.addonCard);
    const select = card && card.querySelector(selectors.addonVariant);
    const variantId = String(swatch.dataset.variantId || '').trim();

    if (!select || !variantId) {
      return;
    }

    const matchingOption = Array.from(select.options).find(option => String(option.value) === variantId);

    if (!(matchingOption && !matchingOption.disabled)) {
      return;
    }

    select.value = variantId;
    updateVariantCard(select);
  });

  document.addEventListener(
    'click',
    event => {
      const ajaxSubmit = event.target.closest(selectors.ajaxSubmit);

      if (!ajaxSubmit) {
        return;
      }

      const form = ajaxSubmit.closest('form');

      if (!maybeHandleForm(form, ajaxSubmit)) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );

  document.addEventListener(
    'submit',
    event => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      if (!maybeHandleForm(form, form.querySelector(selectors.addToCartButton))) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );

  function getAddonRoots(scope) {
    const searchRoot = scope || document;
    const roots = [];

    if (searchRoot.matches && searchRoot.matches(selectors.addonsRoot)) {
      roots.push(searchRoot);
    }

    return roots.concat(Array.from(searchRoot.querySelectorAll(selectors.addonsRoot)));
  }

  function initializeAddonCards(scope) {
    getAddonRoots(scope).forEach(addonsRoot => {
      addonsRoot.querySelectorAll(selectors.addonCard).forEach(card => {
        const variantSelect = card.querySelector(selectors.addonVariant);

        if (variantSelect) {
          updateVariantCard(variantSelect, { preserveImage: true });
        } else {
          syncAvailability(card, !card.classList.contains('is-unavailable'));
        }

        syncToggleState(card);
      });

      renderPurchaseSummary(addonsRoot);
      loadRelatedFallback(addonsRoot);
    });
  }

  function initializeSummary() {
    observeStickyButtons();

    // sections.js defines selectCallback on load; if we get here first, keep
    // trying on the later lifecycle events rather than giving up.
    patchSelectCallback();
  }

  document.addEventListener('DOMContentLoaded', () => {
    initializeSummary();
    initializeAddonCards(document);
  });

  window.addEventListener('load', () => {
    initializeSummary();
  });

  document.addEventListener('shopify:section:load', event => {
    initializeSummary();
    initializeAddonCards(event.target);
  });
})();
